import { z } from "zod";
import {
	CharterSubtype,
	CharterType,
	ContractStatus,
	LabelType,
	LocationType,
	NoticePeriod,
} from "__generated__/enums";
import { AgreementValidationSchema } from "view/agreements/validation/agreement.zod";
import { ContractOptionValidationSchema } from "view/contractsNew/validation/contractoption.zod";
import { ZodSchema } from "@validation/utils/schemaUtils";
import { ApplicationDefinedStatusSchema } from "view/statuses/utils/schema";
import { ContractInput } from "__generated__/graphql";
import { DateUtils } from "utils/DateUtils";
import { convertAgreementFormToInput } from "view/agreements/utils/AgreementUtils";
import { preHandleAgreements } from "view/contractsNew/utils/ContractUtils";
import { intl } from "LocaleWrapper";

export type ContractFormInput = z.input<typeof ContractValidationSchema>;
export type ContractFormOutput = z.output<typeof ContractValidationSchema>;

const ContractRelationSchema = z.object({
	id: z.number(),
	name: z.string(),
	contacts: z.array(z.object({
		id: z.number(),
		name: z.string().nullish(),
		email: z.string().nullish(),
	})).nullish(),
	email: z.string().nullish(),
	applicationRelation: z.boolean().nullish(),
});

export const ContractValidationSchema = z.object({
	id: z.number().nullish(),
	contractNumber: z.string(),
	status: ZodSchema.requiredField(ApplicationDefinedStatusSchema(ContractStatus))
		.transform(status => status.name),
	charterType: z.enum(CharterType),
	charterSubtype: z.enum(CharterSubtype),
	description: z.string().trim().min(1).max(255),
	memo: z.string(),
	labels: z.array(z.object({
		id: z.number(),
		type: z.enum(LabelType),
		text: z.string(),
		color: z.string(),
	}).transform(label => ({ id: label.id }))),
	currency: z.object({
		isoCode: z.string().nullish(),
		rate: z.number().nullish(),
		symbol: z.string().nullish(),
	}).nullish(),
	creditor: ZodSchema.requiredField(ContractRelationSchema).transform(creditor => ({
		id: creditor.id,
	})),
	creditorContact: z.object({
		id: z.number(),
		name: z.string().nullish(),
		email: z.string().nullish(),
	}).nullish(),
	debtor: ZodSchema.requiredField(ContractRelationSchema).transform(debtor => ({
		id: debtor.id,
	})),
	debtorContact: z.object({
		id: z.number(),
		name: z.string().nullish(),
		email: z.string().nullish(),
	}).nullish(),
	broker: ContractRelationSchema.nullish().transform(broker => broker ? ({
		id: broker.id,
	}) : null),
	validWindow: z.tuple([ z.date().nullable(), z.date().nullable() ]).nullish(),
	noticePeriod: z.enum(NoticePeriod).nullish(),
	versionNumber: z.string(),
	allIn: z.boolean(),
	regions: z.array(z.object({
		id: z.number(),
		description: z.string(),
		type: z.enum(LocationType),
		locationGroups: z.array(z.object({
			id: z.number(),
			description: z.string(),
			type: z.enum(LocationType),
		})),
	}).transform(region => ({ id: region.id }))),
	productCategory: z.object({
		id: z.number(),
		name: z.string(),
	}).nullish().transform(productCategory => productCategory ? ({ id: productCategory.id }) : null),
	isParentContract: z.boolean(),
	parentContract: z.object({
		id: z.number(),
		contractNumber: z.string(),
	}).nullable().transform(_ => undefined),
	isBackToBack: z.boolean().nullish(),
	includePricingInNomination: z.boolean().nullish(),
	charters: z.array(z.object({
		id: z.number(),
		validFrom: z.number().nullish(),
		validUntil: z.number().nullish(),
	})).nullish(),
	contractOptions: z.array(ContractOptionValidationSchema),
	takenOptionContract: z.object({
		id: z.number(),
		contractNumber: z.string(),
	}).nullable().transform(_ => undefined),
	agreements: z.array(AgreementValidationSchema),
	termsAndConditions: z.object({
		id: z.number(),
	}).nullish(),
}).refine(contract => {
	if (contract.charterSubtype === CharterSubtype.Spot) {
		return true;
	}
	return !!contract.validWindow && !!contract.validWindow[0] && !!contract.validWindow[1];
}, {
	message: intl.formatMessage({ id: "REQUIRED" }),
	path: [ "validWindow" ],
	when(payload): boolean {
		return ContractValidationSchema.in
			.pick({ charterSubtype: true, validWindow: true })
			.safeParse(payload.value).success;
	},
}).transform(contract => ({
	...{
		...contract,
		creditorContact: undefined,
		debtorContact: undefined,
		validWindow: undefined,
	},
	creditorContactId: contract.creditorContact?.id,
	debtorContactId: contract.debtorContact?.id,
	validFrom: DateUtils.getTimestamp(contract.validWindow?.[0]),
	validUntil: DateUtils.getTimestamp(contract.validWindow?.[1]),
	agreements: preHandleAgreements(contract.agreements).map(agreement => convertAgreementFormToInput(
		agreement, contract.charterType, contract.charterSubtype,
	)),
}) satisfies ContractInput);

export type ContractStatusChangerInput = z.input<typeof ContractStatusChangerSchema>;
export type ContractStatusChangerOutput = z.output<typeof ContractStatusChangerSchema>;

export const ContractStatusChangerSchema = z.object({
	selectedStatus: ZodSchema.requiredField(ApplicationDefinedStatusSchema(ContractStatus)),
});
